import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Plus, Edit3, Trash2, X, Upload, Image as ImageIcon, Languages } from 'lucide-react';
import { projectsApi } from '../../api/projects';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://ayonkdesigns.com/api';
const IMG_BASE = BASE_URL.replace('/api', '');

// ── THE FIX: Keyless, CORS-friendly Translation API ──
const translateWithAI = async (texts) => {
  const translated = {};
  try {
    for (const [key, text] of Object.entries(texts)) {
      if (!text || text.trim() === '') {
        translated[key] = '';
        continue;
      }
      // Uses the free Google Translate extension endpoint (no API key needed, frontend safe)
      const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=ar&tl=en&dt=t&q=${encodeURIComponent(text)}`);
      const data = await response.json();
      translated[key] = data[0].map(item => item[0]).join('');
    }
    return translated;
  } catch (err) {
    console.error('Translation failed:', err);
    return {};
  }
};

export function ProjectsManagement() {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [scopeInput, setScopeInput] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '', category: 'Interior', description: '',
    location: '', area: '', client: '', duration: '',
    challenge: '', result: '', isFeatured: false, completedAt: '',
    titleEn: '', descriptionEn: '', locationEn: '',
    challengeEn: '', resultEn: '', scopeOfWorkEn: ''
  });
  const coverInputRef = useRef();
  const galleryInputRef = useRef();

  const fetchProjects = async () => {
    try { setLoading(true); const data = await projectsApi.getAll(); setProjects(Array.isArray(data) ? data : []); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleOpenModal = (project = null) => {
    if (project) {
      setSelectedProject(project);
      setFormData({
        title: project.title || '', category: project.category || 'Interior',
        description: project.description || '', location: project.location || '',
        area: project.area || '', client: project.client || '',
        duration: project.duration || '', challenge: project.challenge || '',
        result: project.result || '', isFeatured: project.isFeatured || false,
        completedAt: project.completedAt ? project.completedAt.substring(0, 10) : '',
        titleEn: project.titleEn || '', descriptionEn: project.descriptionEn || '',
        locationEn: project.locationEn || '', challengeEn: project.challengeEn || '',
        resultEn: project.resultEn || '', scopeOfWorkEn: project.scopeOfWorkEn || ''
      });
      setScopeInput(project.scopeOfWork || '');
    } else {
      setSelectedProject(null);
      setFormData({
        title: '', category: 'Interior', description: '', location: '', area: '',
        client: '', duration: '', challenge: '', result: '', isFeatured: false,
        completedAt: '', titleEn: '', descriptionEn: '', locationEn: '',
        challengeEn: '', resultEn: '', scopeOfWorkEn: ''
      });
      setScopeInput('');
    }
    setCoverImage(null);
    setGalleryImages([]);
    setIsModalOpen(true);
  };

  const handleAutoTranslate = async () => {
    setTranslating(true);
    try {
      const toTranslate = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        challenge: formData.challenge,
        result: formData.result,
        scopeOfWork: scopeInput
      };
      
      const translated = await translateWithAI(toTranslate);
      
      setFormData(prev => ({
        ...prev,
        titleEn: translated.title || prev.titleEn,
        descriptionEn: translated.description || prev.descriptionEn,
        locationEn: translated.location || prev.locationEn,
        challengeEn: translated.challenge || prev.challengeEn,
        resultEn: translated.result || prev.resultEn,
        scopeOfWorkEn: translated.scopeOfWork || prev.scopeOfWorkEn
      }));
    } catch (err) { console.error(err); }
    finally { setTranslating(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        scopeOfWork: scopeInput,
        finishingType: formData.category,
        order: 0,
        completedAt: formData.completedAt ? new Date(formData.completedAt).toISOString() : null
      };
      let projectId;
      if (selectedProject) {
        await projectsApi.update(selectedProject.id, payload);
        projectId = selectedProject.id;
      } else {
        const created = await projectsApi.create(payload);
        projectId = created.id;
      }
      await fetchProjects();
      setIsModalOpen(false);
      if (coverImage) {
        try { await projectsApi.uploadImage(projectId, coverImage, true); } catch (err) { console.error(err); }
      }
      for (const img of galleryImages) {
        try { await projectsApi.uploadImage(projectId, img, false); } catch (err) { console.error(err); }
      }
      await fetchProjects();
    } catch (err) {
      console.error(err);
      alert(isRtl ? 'حدث خطأ أثناء الحفظ' : 'Error saving project');
    } finally { setSaving(false); }
  };

  const handleDeleteImage = async (projectId, imageId) => {
    if (!window.confirm(isRtl ? 'حذف الصورة؟' : 'Delete image?')) return;
    try {
      await projectsApi.deleteImage(projectId, imageId);
      const updated = await projectsApi.getById(projectId);
      setSelectedProject(updated);
      await fetchProjects();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRtl ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try { await projectsApi.delete(id); await fetchProjects(); } catch (err) { console.error(err); }
  };

  const currentCover = selectedProject?.images?.find(i => i.isPrimary);
  const currentGallery = selectedProject?.images?.filter(i => !i.isPrimary) || [];

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, direction: 'rtl' };
  const inputEnStyle = { ...inputStyle, direction: 'ltr', background: '#f0fdf4', borderColor: '#86efac' };

  return (
    <div className="admin-page-content" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="admin-page-header">
        <h1>{isRtl ? 'إدارة المشاريع' : 'Projects Management'}</h1>
        <button className="btn-add-primary" onClick={() => handleOpenModal()}>
          <Plus size={18} /><span>{isRtl ? 'إضافة مشروع' : 'Add Project'}</span>
        </button>
      </header>

      <div className="admin-content-card">
        {loading ? <p style={{ padding: 20, textAlign: 'center' }}>Loading...</p> :
          projects.length === 0 ? <p style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>{isRtl ? 'لا توجد مشاريع' : 'No projects yet'}</p> :
          <table className="admin-table">
            <thead><tr>
              <th>{isRtl ? 'الصورة' : 'Image'}</th>
              <th>{isRtl ? 'المشروع' : 'Project'}</th>
              <th>{isRtl ? 'التصنيف' : 'Category'}</th>
              <th>{isRtl ? 'الموقع' : 'Location'}</th>
              <th>{isRtl ? 'الإجراءات' : 'Actions'}</th>
            </tr></thead>
            <tbody>{projects.map(p => {
              const cover = p.images?.find(i => i.isPrimary) || p.images?.[0];
              return (
                <tr key={p.id}>
                  <td>{cover ? <img src={`${IMG_BASE}${cover.imageUrl}`} alt={p.title} style={{ width: 55, height: 45, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 55, height: 45, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={18} color="#94a3b8" /></div>}</td>
                  <td style={{ fontWeight: 700 }}>{p.title}{p.titleEn && <span style={{ display: 'block', fontSize: 11, color: '#64748b', fontWeight: 400 }}>{p.titleEn}</span>}</td>
                  <td>{p.category}</td>
                  <td>{p.location || '—'}</td>
                  <td className="actions-cell">
                    <button className="btn-icon edit" onClick={() => handleOpenModal(p)}><Edit3 size={18} /></button>
                    <button className="btn-icon delete" onClick={() => handleDelete(p.id)}><Trash2 size={18} /></button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        }
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: 820, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>{selectedProject ? 'تعديل مشروع | Edit Project' : 'مشروع جديد | New Project'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}><X size={24} /></button>
            </div>
            <form className="admin-form" onSubmit={handleSave}>

              {/* Basic Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-field">
                  <label>اسم المشروع (عربي) *</label>
                  <input style={inputStyle} type="text" required value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="مثال: تصميم داخلي فاخر" />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Project Name (English)</label>
                  <input style={inputEnStyle} type="text" value={formData.titleEn} onChange={e => setFormData({ ...formData, titleEn: e.target.value })} placeholder="e.g. Luxury Interior Design" />
                </div>

                <div className="form-field">
                  <label>التصنيف | Category</label>
                  <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                    <option value="Interior">تصميم داخلي | Interior</option>
                    <option value="Construction">إنشاءات | Construction</option>
                    <option value="Facades">واجهات | Facades</option>
                    <option value="Finishing">تشطيب | Finishing</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>مميز | Featured</label>
                  <select value={formData.isFeatured} onChange={e => setFormData({ ...formData, isFeatured: e.target.value === 'true' })}>
                    <option value="false">لا | No</option>
                    <option value="true">نعم | Yes</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>الموقع (عربي)</label>
                  <input style={inputStyle} type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="القاهرة، الرياض..." />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Location (English)</label>
                  <input style={inputEnStyle} type="text" value={formData.locationEn} onChange={e => setFormData({ ...formData, locationEn: e.target.value })} placeholder="Cairo, Riyadh..." />
                </div>

                <div className="form-field">
                  <label>المساحة | Area</label>
                  <input style={inputStyle} type="text" value={formData.area} onChange={e => setFormData({ ...formData, area: e.target.value })} placeholder="450 م²" />
                </div>
                <div className="form-field">
                  <label>العميل | Client</label>
                  <input style={inputStyle} type="text" value={formData.client} onChange={e => setFormData({ ...formData, client: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>مدة التنفيذ | Duration</label>
                  <input style={inputStyle} type="text" value={formData.duration} onChange={e => setFormData({ ...formData, duration: e.target.value })} placeholder="6 أشهر | 6 months" />
                </div>
                <div className="form-field">
                  <label>تاريخ الإنجاز | Completion Date</label>
                  <input type="date" value={formData.completedAt} onChange={e => setFormData({ ...formData, completedAt: e.target.value })} />
                </div>
              </div>

              {/* Description */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                <div className="form-field">
                  <label>وصف المشروع (عربي)</label>
                  <textarea rows="4" style={inputStyle} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="اكتب وصفاً مختصراً للمشروع..." />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Description (English)</label>
                  <textarea rows="4" style={inputEnStyle} value={formData.descriptionEn} onChange={e => setFormData({ ...formData, descriptionEn: e.target.value })} placeholder="Write a brief project description..." />
                </div>

                <div className="form-field">
                  <label>نطاق العمل (عربي) — سطر لكل بند</label>
                  <textarea rows="4" style={inputStyle} value={scopeInput} onChange={e => setScopeInput(e.target.value)} placeholder="تصميم الأثاث&#10;الإضاءة&#10;الألوان" />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Scope of Work (English) — one per line</label>
                  <textarea rows="4" style={inputEnStyle} value={formData.scopeOfWorkEn} onChange={e => setFormData({ ...formData, scopeOfWorkEn: e.target.value })} placeholder="Furniture Design&#10;Lighting&#10;Colors" />
                </div>

                <div className="form-field">
                  <label>التحدي (عربي)</label>
                  <textarea rows="3" style={inputStyle} value={formData.challenge} onChange={e => setFormData({ ...formData, challenge: e.target.value })} />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Challenge (English)</label>
                  <textarea rows="3" style={inputEnStyle} value={formData.challengeEn} onChange={e => setFormData({ ...formData, challengeEn: e.target.value })} />
                </div>

                <div className="form-field">
                  <label>النتيجة (عربي)</label>
                  <textarea rows="3" style={inputStyle} value={formData.result} onChange={e => setFormData({ ...formData, result: e.target.value })} />
                </div>
                <div className="form-field">
                  <label style={{ color: '#16a34a' }}>Result (English)</label>
                  <textarea rows="3" style={inputEnStyle} value={formData.resultEn} onChange={e => setFormData({ ...formData, resultEn: e.target.value })} />
                </div>
              </div>

              {/* Auto Translate Button */}
              <div style={{ textAlign: 'center', margin: '16px 0', padding: '12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
                <button type="button" onClick={handleAutoTranslate} disabled={translating}
                  style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Languages size={18} />
                  {translating ? 'جاري الترجمة...' : '🤖 ترجمة تلقائية | Auto Translate'}
                </button>
                <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>انقر لترجمة الحقول العربية تلقائياً إلى الإنجليزية | Click to auto-translate Arabic fields to English</p>
              </div>

              {/* Cover Image */}
              <div style={{ borderTop: '2px solid #fee2e2', paddingTop: 16, marginTop: 8 }}>
                <label style={{ color: '#e53e3e', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 8 }}>
                  📷 صورة الغلاف | Cover Image (Hero Photo)
                </label>
                {currentCover && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <img src={`${IMG_BASE}${currentCover.imageUrl}`} alt="" style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 8, border: '3px solid #e53e3e' }} />
                    <button type="button" onClick={() => handleDeleteImage(selectedProject.id, currentCover.id)} style={{ fontSize: 12, color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}>🗑 حذف | Delete</button>
                  </div>
                )}
                <div onClick={() => coverInputRef.current?.click()} style={{ border: '2px dashed #e53e3e', borderRadius: 8, padding: 14, textAlign: 'center', cursor: 'pointer', background: '#fff5f5' }}>
                  <Upload size={22} style={{ margin: '0 auto 4px', color: '#e53e3e' }} />
                  <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>انقر لاختيار صورة الغلاف | Click to select cover image</p>
                  <input ref={coverInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.HEIC" style={{ display: 'none' }} onChange={e => { if(e.target.files[0]) setCoverImage(e.target.files[0]); }} />
                </div>
                {coverImage && (
                  <div style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
                    <img src={URL.createObjectURL(coverImage)} alt="" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '3px solid #48bb78' }} />
                    <button type="button" onClick={() => setCoverImage(null)} style={{ position: 'absolute', top: -8, right: -8, background: '#e53e3e', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer' }}>×</button>
                  </div>
                )}
              </div>

              {/* Gallery Images */}
              <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 16, marginTop: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 8 }}>
                  🖼️ معرض الصور | Gallery Images
                </label>
                {currentGallery.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    {currentGallery.map(img => (
                      <div key={img.id} style={{ position: 'relative' }}>
                        <img src={`${IMG_BASE}${img.imageUrl}`} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, border: '2px solid #e2e8f0' }} />
                        <button type="button" onClick={() => handleDeleteImage(selectedProject.id, img.id)} style={{ position: 'absolute', top: -8, right: -8, background: '#e53e3e', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div onClick={() => galleryInputRef.current?.click()} style={{ border: '2px dashed #e2e8f0', borderRadius: 8, padding: 14, textAlign: 'center', cursor: 'pointer' }}>
                  <Upload size={22} style={{ margin: '0 auto 4px', color: '#94a3b8' }} />
                  <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>انقر لاختيار صور المعرض | Click to select gallery photos (multiple)</p>
                  <input ref={galleryInputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.HEIC" style={{ display: 'none' }} onChange={e => setGalleryImages(prev => [...prev, ...Array.from(e.target.files)])} />
                </div>
                {galleryImages.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {galleryImages.map((f, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={URL.createObjectURL(f)} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, border: '2px solid #48bb78' }} />
                        <button type="button" onClick={() => setGalleryImages(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -8, right: -8, background: '#e53e3e', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>إلغاء | Cancel</button>
                <button type="submit" className="btn-add-primary" disabled={saving}>{saving ? 'جاري الحفظ... | Saving...' : 'حفظ | Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
